import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { SectionHeading } from '../components/ui';
import { PricingCard, PricingPlan } from '../components/PricingCard';
const PRICING: PricingPlan[] = [{
  id: 'draft',
  title: 'Draft Day Rental',
  price: '$19.99',
  period: '',
  badge: 'TRY IT TONIGHT',
  badgeColor: 'lime',
  borderClass: 'border-lime',
  glowClass: 'hover:shadow-[0_0_30px_rgba(200,255,0,0.2)]',
  description: 'One-night access. Perfect for draft night. Zero commitment.',
  features: ['Live draft reactions', '2 commentators', 'Valid for 24 hours'],
  ctaText: 'RENT FOR TONIGHT',
  ctaClass: 'bg-transparent border-2 border-lime text-lime hover:bg-lime hover:text-charcoal'
}, {
  id: 'starter',
  title: 'Starter',
  price: '$49',
  period: 'season',
  borderClass: 'border-white/20',
  glowClass: 'hover:border-white/50',
  description: '1 league. 2 commentators. Tiers 1-2.',
  features: ['1 Sleeper League', 'Pick 2 commentators', 'Tiers 1 & 2 only', 'Weekly recaps'],
  ctaText: 'START YOUR SEASON',
  ctaClass: 'bg-white text-charcoal hover:bg-slate-200'
}, {
  id: 'playoff',
  title: 'Playoff Push',
  price: '$99',
  period: 'season',
  badge: '⭐ MOST POPULAR',
  badgeColor: 'orange',
  borderClass: 'border-orange',
  glowClass: 'shadow-[0_0_20px_rgba(255,107,0,0.15)] hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] scale-105 z-10',
  description: '1 league. Full character cast. Tiers 1-3 unlocked.',
  features: ['1 Sleeper League', 'All 11 commentators', 'Tiers 1, 2 & 3', 'Live game reactions', 'Custom league name audio'],
  ctaText: 'GO PLAYOFF PUSH',
  ctaClass: 'bg-orange text-white hover:bg-orange/80',
  popular: true
}, {
  id: 'afterdark',
  title: 'After Dark',
  price: '$149',
  period: 'season',
  badge: '🔥 ADULTS ONLY',
  badgeColor: 'crimson',
  borderClass: 'border-crimson',
  glowClass: 'hover:shadow-[0_0_30px_rgba(220,38,38,0.2)]',
  description: '1 league. Full cast. All 4 tiers. Clone upsell included.',
  features: ['1 Sleeper League', 'All 11 commentators', 'Tier 4 (Uncensored) Unlocked', 'Clone your own voice (1)', 'Priority delivery'],
  ctaText: 'UNLOCK AFTER DARK',
  ctaClass: 'bg-crimson text-white hover:bg-crimson/80'
}, {
  id: 'commissioner',
  title: 'Commissioner Pack',
  price: '$199',
  period: 'season',
  badge: '👑 BEST VALUE',
  badgeColor: 'gold',
  borderClass: 'border-gold',
  glowClass: 'hover:shadow-[0_0_30px_rgba(245,197,24,0.2)]',
  description: '3 leagues. Every feature. Every tier. Commissioner goes FREE when 8+ members pay.',
  features: ['Up to 3 Leagues', 'All 11 commentators', 'All Tiers (1-4)', 'Loser Pays mechanic', 'Commissioner goes free'],
  subNote: 'Loser Pays mechanic available — collect upfront, auto-charge last place at season end.',
  ctaText: 'GET COMMISSIONER PACK',
  ctaClass: 'bg-gold text-charcoal hover:bg-yellow-400'
}];
const FAQS = [{
  q: 'Does this work with ESPN or Yahoo?',
  a: 'Currently, we only integrate directly with Sleeper for automated real-time reactions. We are working on ESPN and Yahoo integrations for next season.'
}, {
  q: "How does the 'Loser Pays' mechanic work?",
  a: "If you buy the Commissioner Pack, you can send a link to your league. We authorize everyone's card for $20. At the end of the season, the person in last place gets charged the full amount. Everyone else pays nothing."
}, {
  q: 'Can I change my Hilarious Meter mid-season?',
  a: 'Yes! You can dial the chaos up or down at any point from your dashboard. If your league gets too sensitive, just bump it down to Tier 1.'
}, {
  q: 'Are the voices real people?',
  a: "The voices are generated using advanced AI trained on professional voice actors and comedians. They are dynamic and react specifically to your players' actual stats."
}];
export const PricingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="w-full pt-20" data-id="element-287">
      {/* HEADER */}
      <section className="py-20 bg-charcoal text-center border-b border-white/5" data-id="element-288">
        <div className="container mx-auto px-4" data-id="element-289">
          <SectionHeading title="PICK YOUR SEASON PASS" subtitle="No monthly fees. One payment covers the entire season." className="mb-0 mx-auto flex flex-col items-center" data-id="element-290" />
        </div>
      </section>

      {/* CARDS */}
      <section className="py-20 bg-dark" data-id="element-291">
        <div className="container mx-auto px-4" data-id="element-292">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 items-stretch max-w-[1400px] mx-auto" data-id="element-293">
            {PRICING.map(plan => <PricingCard key={plan.id} plan={plan} data-id="element-294" />)}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24 bg-charcoal border-y border-white/5" data-id="element-295">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-296">
          <h2 className="text-4xl md:text-5xl font-black font-heading uppercase -skew-x-6 mb-12 text-center" data-id="element-297">
            FEATURE COMPARISON
          </h2>

          <div className="overflow-x-auto" data-id="element-298">
            <table className="w-full text-left border-collapse" data-id="element-299">
              <thead data-id="element-300">
                <tr className="border-b-2 border-white/20" data-id="element-301">
                  <th className="p-4 text-slate font-bold uppercase tracking-wider" data-id="element-302">
                    Features
                  </th>
                  <th className="p-4 font-heading font-black text-xl text-center" data-id="element-303">
                    Draft Day
                  </th>
                  <th className="p-4 font-heading font-black text-xl text-center" data-id="element-304">
                    Starter
                  </th>
                  <th className="p-4 font-heading font-black text-xl text-center text-orange" data-id="element-305">
                    Playoff Push
                  </th>
                  <th className="p-4 font-heading font-black text-xl text-center text-crimson" data-id="element-306">
                    After Dark
                  </th>
                  <th className="p-4 font-heading font-black text-xl text-center text-gold" data-id="element-307">
                    Commish Pack
                  </th>
                </tr>
              </thead>
              <tbody className="text-lg" data-id="element-308">
                {[{
                name: 'Leagues Included',
                vals: ['1 (24hrs)', '1', '1', '1', '3']
              }, {
                name: 'Characters',
                vals: ['2', '2', 'All 11', 'All 11', 'All 11']
              }, {
                name: 'Tiers Unlocked',
                vals: ['1-3', '1-2', '1-3', '1-4', '1-4']
              }, {
                name: 'Draft Night Audio',
                vals: [true, false, true, true, true]
              }, {
                name: 'Loser Pays Mechanic',
                vals: [false, false, false, false, true]
              }, {
                name: 'Clone Upsell',
                vals: [false, false, false, true, true]
              }].map((row, i) => <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-309">
                    <td className="p-4 font-medium text-slate" data-id="element-310">{row.name}</td>
                    {row.vals.map((val, j) => <td key={j} className="p-4 text-center" data-id="element-311">
                        {typeof val === 'boolean' ? val ? <Check className="mx-auto text-lime" size={20} data-id="element-312" /> : <span className="text-slate/30" data-id="element-313">—</span> : <span className="font-bold" data-id="element-314">{val}</span>}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* AFFILIATE SECTION */}
      <section className="py-24 bg-lime text-charcoal relative overflow-hidden" data-id="element-315">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" data-id="element-316"></div>
        <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl" data-id="element-317">
          <h2 className="text-5xl md:text-7xl font-black font-heading uppercase -skew-x-6 mb-6" data-id="element-318">
            MAKE MONEY RUNNING LEAGUES
          </h2>
          <p className="text-2xl font-bold mb-12" data-id="element-319">
            Share your link. Every commissioner you refer pays you every season.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-320">
            <div className="bg-charcoal text-white p-8 rounded-2xl border-4 border-dark" data-id="element-321">
              <h3 className="font-heading font-black text-2xl text-lime mb-2" data-id="element-322">
                STANDARD
              </h3>
              <p className="text-4xl font-black font-mono mb-2" data-id="element-323">20%</p>
              <p className="text-slate text-sm" data-id="element-324">
                Recurring commission on all referrals
              </p>
            </div>
            <div className="bg-charcoal text-white p-8 rounded-2xl border-4 border-dark transform md:-translate-y-4 shadow-2xl" data-id="element-325">
              <h3 className="font-heading font-black text-2xl text-orange mb-2" data-id="element-326">
                CREATOR
              </h3>
              <p className="text-4xl font-black font-mono mb-2" data-id="element-327">30%</p>
              <p className="text-slate text-sm" data-id="element-328">
                For podcasts, YouTubers, and influencers
              </p>
            </div>
            <div className="bg-charcoal text-white p-8 rounded-2xl border-4 border-dark" data-id="element-329">
              <h3 className="font-heading font-black text-2xl text-blue-400 mb-2" data-id="element-330">
                ORG / SCHOOL
              </h3>
              <p className="text-4xl font-black font-mono mb-2" data-id="element-331">CUSTOM</p>
              <p className="text-slate text-sm" data-id="element-332">
                Special rates for fundraising
              </p>
            </div>
          </div>

          <button className="bg-charcoal text-lime px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-dark hover:scale-105 transition-all" data-id="element-333">
            Join Affiliate Program
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-dark" data-id="element-334">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-335">
          <SectionHeading title="FREQUENTLY ASKED QUESTIONS" className="text-center mb-12" data-id="element-336" />

          <div className="space-y-4" data-id="element-337">
            {FAQS.map((faq, i) => <div key={i} className="bg-charcoal border border-white/10 rounded-2xl overflow-hidden" data-id="element-338">
                <button className="w-full p-6 text-left flex justify-between items-center hover:bg-white/5 transition-colors" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-339">
                  <span className="font-bold text-lg pr-8" data-id="element-340">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="text-lime shrink-0" data-id="element-341" /> : <ChevronDown className="text-slate shrink-0" data-id="element-342" />}
                </button>
                <AnimatePresence data-id="element-343">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-344">
                      <div className="p-6 pt-0 text-slate leading-relaxed border-t border-white/5 mt-2" data-id="element-345">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
};